import React, { useContext, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import Dropdown from 'react-bootstrap/Dropdown'
import { toast } from 'react-toastify'
import reportApiFailure from 'shared/api/reportApiFailure'
import { useTranslation } from 'react-i18next'
import { DeclarationStatusEnum } from 'types/IDeclaration'
import { UserContext } from 'shared/users/contexts/UserContext'
import { TransitOperation } from 'features/declaration/transit-operation/models/types'
import { TransitOperationInfoResponse } from 'api/models'
import NotesModal from 'routes/phase5/common/components/NotesModal/NotesModal'
import { extractCountryFromDepartureOffice } from 'shared/ncts/common/classifier-util'
import { createTransitOperationInfo } from '../transit-operation-info/api/createTransitOperationInfo'
import { updateReviewStatus } from '../transit-operation-info/api/updateReviewStatus'
import NonArrivedMovementReleaseModal from '../../transit-operation/components/NonArrivedMovementReleaseModal'
import useReleaseNonArrivedMovementModal from '../../transit-operation/components/useReleaseNonArrivedMovementModal'
import useForceTransitOperationStatus from '../../transit-operation/api/forceTransitOperationStatus'
import FeedbackModal from './feedback/FeedbackModal'
import { QueryKeys } from '../../../key-registry'

interface TransitPreviewActionsProps {
  currentView: string
  transitOperation: TransitOperation | undefined
  originalRow: any
  transitOperationId: number
  transitOperationInfo: TransitOperationInfoResponse | undefined
  isAuditView: boolean
}

function TransitPreviewActions(props: Readonly<TransitPreviewActionsProps>) {
  const {
    currentView, transitOperation, originalRow, transitOperationId, transitOperationInfo, isAuditView,
  } = props

  const [isReviewed, setIsReviewed] = useState(transitOperationInfo?.isReviewed)
  const [isNotesOpen, setIsNotesOpen] = useState<boolean>(false)
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState<boolean>(false)
  const nonArrivedMovementRelease = useReleaseNonArrivedMovementModal(transitOperationId)

  const { user } = useContext(UserContext)
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const invalidateTransitOperationInfo = () => queryClient.invalidateQueries({
    queryKey: QueryKeys.transitOperationInfo.detail(transitOperationId),
  })

  useEffect(() => {
    setIsReviewed(originalRow.isReviewed)
  }, [transitOperationId])

  const toggleDeclarationReview = async () => {
    if (transitOperation && originalRow) {
      if (originalRow?.transitOperationInfoId === null) {
        await createTransitOperationInfo(transitOperationId).then(async (response) => {
          if (response) {
            await updateReviewStatus(transitOperationId, !originalRow.isReviewed)
              .then((resp) => {
                originalRow.isReviewed = resp.isReviewed
                setIsReviewed(resp.isReviewed)
                invalidateTransitOperationInfo()
              })
              .catch((error) => {
                toast.error('Updating Review Status failed!')
                // eslint-disable-next-line no-console
                console.error(error)
              })
          }
        })
      } else {
        await updateReviewStatus(transitOperationId, !originalRow.isReviewed)
          .then((resp) => {
            originalRow.isReviewed = resp.isReviewed
            setIsReviewed(resp.isReviewed)
            invalidateTransitOperationInfo()
          })
          .catch((error) => {
            toast.error('Updating Review Status failed!')
            // eslint-disable-next-line no-console
            console.error(error)
          })
      }
    }
  }

  const { forceStatusForTransitOperation } = useForceTransitOperationStatus()

  const updateStatusForTransitOperation = async () => {
    await forceStatusForTransitOperation(transitOperationId)
      .then(() => {
        toast.success('Transit Successfully closed!')
      })
      .catch((error) => {
        reportApiFailure(error, () => toast.error(t('transitOperation.forceCloseError')))
      })
  }

  const country = transitOperation
    ? extractCountryFromDepartureOffice(transitOperation.departureCustomsOffice ?? null)
    : null

  const status = transitOperation?.status ?? ''

  const isNonArrivedMovementReleaseRequired = (): boolean => {
    if (country !== 'RO') return false

    return ['NON_ARRIVED_MOVEMENT'].includes(status as string)
  }

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle variant="link" className="p-0 hide-caret" id="dropdown-basic">
          <i className="fas fa-ellipsis-v fa-md" />
        </Dropdown.Toggle>
        <Dropdown.Menu className="shadow-sm">
          <Dropdown.Item
            disabled={!(originalRow?.mrn || originalRow?.lrn)}
            onClick={() => {
              navigator.clipboard.writeText(`${process.env.REACT_APP_URL}/guarantee-status?mrn=${originalRow?.mrn ?? ''}&lrn=${originalRow?.lrn ?? ''}`)
            }}
          >
            <i className="fal fa-clipboard-check me-2 fa-md" />
            <span>{t('buttons.copyLink', { context: 'guaranteeClaim' })}</span>
          </Dropdown.Item>
          {
            (user?.role === 'ADMIN' && currentView !== 'logs' && (
              <>
                {(isAuditView || originalRow?.status === DeclarationStatusEnum.IN_REVIEW || isReviewed) && (
                  <Dropdown.Item
                    type="button"
                    className="btn-lg"
                    onClick={toggleDeclarationReview}
                  >
                    <i className={`${!isReviewed ? 'fa-check' : 'fa-times'} fas me-1`} />
                    <span>{t('buttons.review', { context: !isReviewed ? 'approved' : 'decline' })}</span>
                  </Dropdown.Item>
                )}
                <Dropdown.Item
                  className="btn-lg"
                  style={{ color: '#1a1b1e' }}
                  onClick={() => setIsNotesOpen(true)}
                >
                  <i className="fal fa-envelope me-2" />
                  <span>{t('buttons.notes')}</span>
                </Dropdown.Item>
                {originalRow?.status !== DeclarationStatusEnum.T1_TRANSIT_CLOSED && (
                  <Dropdown.Item
                    className="btn-lg"
                    onClick={() => updateStatusForTransitOperation()}
                  >
                    <i className="fal fa-check me-2" />
                    <span>{t('buttons.t1Closed')}</span>
                  </Dropdown.Item>
                )}
                <Dropdown.Item
                  className="btn-lg"
                  onClick={() => {
                    setIsFeedbackModalOpen(true)
                  }}
                >
                  <i className="fal fa-info me-2" />
                  <span>
                    {t('buttons.feedback')}
                  </span>
                </Dropdown.Item>
                {isNonArrivedMovementReleaseRequired() ? (
                  <Dropdown.Item
                    className="btn-lg"
                    onClick={() => {
                      nonArrivedMovementRelease.open()
                    }}
                  >
                    <i className="fal fa-reply me-2 fa-md" />
                    <span>Non-arrived movement release (IE141)</span>
                  </Dropdown.Item>
                ) : null}
              </>
            ))
          }

        </Dropdown.Menu>
      </Dropdown>

      <NotesModal
        transitOperationId={transitOperationId}
        isNotesModalOpen={isNotesOpen}
        setIsNotesModalOpen={setIsNotesOpen}
        originalRow={originalRow}
      />
      <FeedbackModal
        transitFeedback={transitOperationInfo?.feedback ?? ''}
        transitOperation={transitOperation}
        transitOperationId={transitOperationId}
        isFeedbackModalOpen={isFeedbackModalOpen}
        setIsFeedbackModalOpen={setIsFeedbackModalOpen}
      />
      <NonArrivedMovementReleaseModal
        show={nonArrivedMovementRelease.isOpen}
        onClose={nonArrivedMovementRelease.close}
        onSubmit={nonArrivedMovementRelease.submit}
        isSubmitting={nonArrivedMovementRelease.isSubmitting}
        isError={nonArrivedMovementRelease.isError}
      />
    </>
  )
}

export default TransitPreviewActions
